export enum PollyLanguage {
  ARABIC = 'arb',
  CHINESE = 'cn',
  CZECH = 'cz',
  DANISH = 'dk',
  DUTCH = 'nl',
  ENGLISH = 'en',
  FINNISH = 'fi',
  FRENCH = 'fr',
  GERMAN = 'de',
  HINDI = 'in',
  ICELANDIC = 'is',
  ITALIAN = 'it',
  JAPANESE = 'jp',
  KOREAN = 'kr',
  NORWEGIAN = 'no',
  POLISH = 'pl',
  PORTUGUESE = 'pt',
  ROMANIAN = 'ro',
  RUSSIAN = 'ru',
  SPANISH = 'es',
  SWEDISH = 'se',
  TURKISH = 'tr',
  WELSH = 'gb',
}

export enum PollyEngine {
  NEURAL = 'neural',
  STANDARD = 'standard',
  LONG_FORM = 'long-form',
  GENERATIVE = 'generative',
}

export const POLLY_LANGUAGE_LABELS: Record<PollyLanguage, string> = {
  [PollyLanguage.ARABIC]: 'Arabic',
  [PollyLanguage.CHINESE]: 'Chinese',
  [PollyLanguage.CZECH]: 'Czech',
  [PollyLanguage.DANISH]: 'Danish',
  [PollyLanguage.DUTCH]: 'Dutch',
  [PollyLanguage.ENGLISH]: 'English',
  [PollyLanguage.FINNISH]: 'Finnish',
  [PollyLanguage.FRENCH]: 'French',
  [PollyLanguage.GERMAN]: 'German',
  [PollyLanguage.HINDI]: 'Hindi',
  [PollyLanguage.ICELANDIC]: 'Icelandic',
  [PollyLanguage.ITALIAN]: 'Italian',
  [PollyLanguage.JAPANESE]: 'Japanese',
  [PollyLanguage.KOREAN]: 'Korean',
  [PollyLanguage.NORWEGIAN]: 'Norwegian',
  [PollyLanguage.POLISH]: 'Polish',
  [PollyLanguage.PORTUGUESE]: 'Portuguese',
  [PollyLanguage.ROMANIAN]: 'Romanian',
  [PollyLanguage.RUSSIAN]: 'Russian',
  [PollyLanguage.SPANISH]: 'Spanish',
  [PollyLanguage.SWEDISH]: 'Swedish',
  [PollyLanguage.TURKISH]: 'Turkish',
  [PollyLanguage.WELSH]: 'Welsh',
};

export const DEFAULT_ENGINE = PollyEngine.GENERATIVE;
export const DEFAULT_VOICE_ID = 'Ruth';

/**
 * Sample line the assistant speaks when a voice is auditioned, in each
 * language it can be configured for. Lives here rather than in either app so
 * the CMS's project-level picker and the hub's per-configuration one audition
 * a voice with the same sentence.
 */
export const testAudioText: Record<PollyLanguage, (name: string) => string> = {
  [PollyLanguage.ARABIC]: (name) =>
    `مرحباً، أنا ${name}، مساعدك الإرشادي الافتراضي`,
  [PollyLanguage.CHINESE]: (name) => `你好，我是${name}，您的虚拟导览助手`,
  [PollyLanguage.CZECH]: (name) =>
    `Dobrý den, jsem ${name}, váš virtuální průvodce`,
  [PollyLanguage.DANISH]: (name) =>
    `Hej, jeg er ${name}, din virtuelle guide-assistent`,
  [PollyLanguage.DUTCH]: (name) =>
    `Hallo, ik ben ${name}, uw virtuele gids-assistent`,
  [PollyLanguage.ENGLISH]: (name) =>
    `Hello, I'm ${name}, your virtual guide assistant`,
  [PollyLanguage.FINNISH]: (name) =>
    `Hei, olen ${name}, virtuaalinen opasassistenttisi`,
  [PollyLanguage.FRENCH]: (name) =>
    `Bonjour, je suis ${name}, votre assistant guide virtuel`,
  [PollyLanguage.GERMAN]: (name) =>
    `Hallo, ich bin ${name}, dein virtueller Guide-Assistent`,
  [PollyLanguage.HINDI]: (name) =>
    `नमस्ते, मैं ${name} हूँ, आपका वर्चुअल गाइड असिस्टेंट`,
  [PollyLanguage.ICELANDIC]: (name) =>
    `Halló, ég er ${name}, sýndarleiðsögumaðurinn þinn`,
  [PollyLanguage.ITALIAN]: (name) =>
    `Ciao, sono ${name}, il tuo assistente guida virtuale`,
  [PollyLanguage.JAPANESE]: (name) =>
    `こんにちは、私は${name}、あなたのバーチャルガイドアシスタントです`,
  [PollyLanguage.KOREAN]: (name) =>
    `안녕하세요, 저는 ${name}입니다, 당신의 가상 가이드 어시스턴트입니다`,
  [PollyLanguage.NORWEGIAN]: (name) =>
    `Hei, jeg er ${name}, din virtuelle guide-assistent`,
  [PollyLanguage.POLISH]: (name) =>
    `Cześć, jestem ${name}, twoim wirtualnym asystentem przewodnika`,
  [PollyLanguage.PORTUGUESE]: (name) =>
    `Olá, eu sou ${name}, seu assistente guia virtual`,
  [PollyLanguage.ROMANIAN]: (name) =>
    `Bună, sunt ${name}, asistentul tău virtual de ghidare`,
  [PollyLanguage.RUSSIAN]: (name) =>
    `Здравствуйте, я ${name}, ваш виртуальный помощник-гид`,
  [PollyLanguage.SPANISH]: (name) =>
    `Hola, soy ${name}, tu asistente guía virtual`,
  [PollyLanguage.SWEDISH]: (name) =>
    `Hej, jag är ${name}, din virtuella guideassistent`,
  [PollyLanguage.TURKISH]: (name) =>
    `Merhaba, ben ${name}, sanal rehber asistanınızım`,
  [PollyLanguage.WELSH]: (name) =>
    `Helo, ${name} ydw i, eich cynorthwyydd tywysydd rhithwir`,
};
